import React, { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { X, Plus, ChevronRight, Info } from "lucide-react";
import { FontSectionProps } from "./types";
import { preloadAllGoogleFonts } from "./data/googleFonts";
import colors from "../../colors/colors";

export const FontSection: React.FC<FontSectionProps> = ({
  themeFonts,
  isExpanded,
  onToggle,
  isLoading,
  onDeleteFont,
  onOpenFontPicker,
  studioLockedFontKeys,
}) => {
  const [fontsPreloaded, setFontsPreloaded] = useState(false);

  // Preload Google Fonts when section expands for the first time
  useEffect(() => {
    if (isExpanded && !fontsPreloaded) {
      preloadAllGoogleFonts().then(() => setFontsPreloaded(true));
    }
  }, [isExpanded, fontsPreloaded]);

  // Format the source label
  const getSourceLabel = (format: string) => {
    switch (format) {
      case "google":
        return "Google Fonts";
      case "system":
        return "System";
      case "woff2":
      case "woff":
      case "truetype":
      case "opentype":
        return "Uploaded";
      default:
        return format;
    }
  };

  return (
    <div className="bc-theme-editor-section">
      <div className="bc-theme-editor-section-header" onClick={onToggle}>
        <h3 className="bc-theme-editor-section-title">
          Fonts
          <span className="bc-theme-editor-section-count">
            {Object.keys(themeFonts).length}
          </span>
        </h3>
        <ChevronRight
          size={16}
          className={`bc-theme-editor-section-chevron ${isExpanded ? "expanded" : ""}`}
        />
      </div>

      {isExpanded && (
        <div className="bc-theme-editor-section-content">
          <div className="bc-theme-editor-list">
            {Object.entries(themeFonts).map(([key, font]) => {
              const isStudioLocked = studioLockedFontKeys?.has(key) ?? false;
              return (
                <div key={key} className="bc-theme-editor-row bc-font-row">
                  <span
                    className="bc-font-preview-name"
                    style={{
                      fontFamily: fontsPreloaded
                        ? `'${font.name}', sans-serif`
                        : "inherit",
                    }}
                  >
                    {font.name}
                  </span>
                  <span className="bc-theme-editor-asset-format">
                    {isStudioLocked ? "Concierge Studio" : getSourceLabel(font.format)}
                  </span>
                  {isStudioLocked ? (
                    <span
                      title="Edit this font from Concierge Studio"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: colors.blue,
                        padding: "4px",
                        cursor: "help",
                      }}
                    >
                      <Info size={14} />
                    </span>
                  ) : (
                    <button
                      className="bc-theme-editor-delete-btn"
                      onClick={() => onDeleteFont(key)}
                      disabled={isLoading}
                      aria-label="Delete font"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              );
            })}

            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={12} />}
              onClick={onOpenFontPicker}
              disabled={isLoading}
            >
              Add Font
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
